import { useState, useEffect } from 'react';
import { userApi } from '../../api/user.api';
import { phoneError, normalizeIranianMobile } from '../../utils/validators';
import { departmentApi } from '../../api/department.api';
import { useUI } from '../../store/UIContext';
import { Avatar, Badge, Btn, EmptyState, Input, Modal, PageHeader, PasswordField, PhoneInput, Select, Skeleton } from '../../components/ui/index';
import { toPersian } from '../../utils/persianNumber';
import { useDebounce } from '../../hooks/useDraft';
import UserActionsModal from '../../components/users/UserActionsModal';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { DownloadSimple, IdentificationBadge } from '@phosphor-icons/react';

const FILTERS = ['همه', 'فعال', 'غیرفعال'];
const emptyForm = { department_name: '', username: '', first_name: '', last_name: '', password: '', password2: '', phone_number: '' };

function exportEmployeesCSV(rows) {
  const cols = ['username', 'first_name', 'last_name', 'department_name', 'active', 'is_manager'];
  const headers = ['نام کاربری', 'نام', 'نام خانوادگی', 'دپارتمان', 'فعال', 'مدیر دپارتمان'];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [headers.join(','), ...rows.map((r) => cols.map((c) => esc(r[c])).join(','))].join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `employees_${Date.now()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function EmployeeListPage() {
  const { addToast } = useUI();
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('همه');
  const [search, setSearch] = useState('');
  const q = useDebounce(search, 300);

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [actionsTarget, setActionsTarget] = useState(null);
  // Only start showing red fields after a submit attempt - not while the
  // user is still filling the form out for the first time.
  const [attempted, setAttempted] = useState(false);
  const [deptError, setDeptError] = useState('');

  const load = (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    userApi.getEmployeeListView()
      .then((res) => setEmployees(Array.isArray(res.data) ? res.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    departmentApi.getDepartmentListView()
      .then((res) => setDepartments(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  useAutoRefresh(() => load(false), { paused: addOpen || !!actionsTarget });

  const filtered = employees.filter((e) => {
    const name = e.username || `${e.first_name || ''} ${e.last_name || ''}`.trim();
    const m = !q || name.includes(q);
    if (filter === 'فعال') return m && !!e.active;
    if (filter === 'غیرفعال') return m && !e.active;
    return m;
  });

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  // Field-level errors, computed live once a submit has been attempted -
  // no toast summarizing "these fields are missing" anymore, each empty
  // field turns red in place and says it needs to be filled.
  const fieldError = (key, label) => (attempted && !String(form[key]).trim() ? `${label} را پر کنید` : '');
  const errors = {
    department_name: fieldError('department_name', 'دپارتمان'),
    username: fieldError('username', 'نام کاربری'),
    first_name: fieldError('first_name', 'نام'),
    last_name: fieldError('last_name', 'نام خانوادگی'),
    password: fieldError('password', 'رمز عبور'),
    password2: fieldError('password2', 'تکرار رمز عبور') || (form.password2 && form.password !== form.password2 ? 'تکرار رمز عبور با رمز عبور یکسان نیست' : ''),
    phone_number: fieldError('phone_number', 'شماره موبایل') || phoneError(form.phone_number),
  };

  const submit = async () => {
    setAttempted(true);
    setDeptError('');
    const hasMissing = !form.department_name || !form.username.trim() || !form.first_name.trim() || !form.last_name.trim() || !form.password.trim() || !form.phone_number.trim() || form.password !== form.password2;
    if (hasMissing || phoneError(form.phone_number)) return;

    setSaving(true);
    try {
      // Backend (addNewEmployeeToDepartment) reads the department name as
      // `name`, not `department_name` - this form's own field name was
      // being sent through as-is, so the backend always saw name=None,
      // matched zero departments, and 501'd on every submit regardless of
      // what was actually selected.
      const { department_name, password2: _p2, ...rest } = form;
      await userApi.addNewEmployeeToDepartment({
        name: department_name,
        ...rest,
        phone_number: normalizeIranianMobile(rest.phone_number),
      });
      addToast('کارمند افزوده شد', 'success');
      setAddOpen(false);
      setForm(emptyForm);
      setAttempted(false);
      load();
    } catch (err) {
      // Backend returns a specific message for an unrecognized department -
      // put it on the department field itself rather than a toast; any
      // other (unexpected/network) failure still needs a toast since it
      // isn't tied to one particular field.
      const msg = typeof err?.response?.data === 'string' ? err.response.data : '';
      if (msg.includes('دپارتمان')) setDeptError(msg);
      else addToast(msg || 'خطا در افزودن کارمند', 'error');
    }
    setSaving(false);
  };

  return (
    <div className="fade-in">
      <PageHeader title="لیست کارمندان" subtitle={`${toPersian(filtered.length)} کارمند`}
        action={
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="secondary" onClick={() => exportEmployeesCSV(filtered)} disabled={filtered.length === 0}><DownloadSimple size={14} weight="regular" /> خروجی اکسل</Btn>
            <Btn onClick={() => setAddOpen(true)}>+ افزودن کارمند</Btn>
          </div>
        }
      />
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} style={{ padding: '7px 16px', borderRadius: 20, border: `1.5px solid ${filter === f ? '#29AAE1' : '#e2e8f0'}`, background: filter === f ? '#EDF3FC' : '#fff', color: filter === f ? '#233A7A' : '#64748b', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>{f}</button>
        ))}
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="جستجو..." style={{ marginRight: 'auto', padding: '7px 14px', border: '1.5px solid #e2e8f0', borderRadius: 20, fontSize: 12, outline: 'none', fontFamily: 'inherit', width: 200 }} />
      </div>

      {loading
        ? <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[1, 2, 3, 4].map((k) => <div key={k} style={{ background: '#fff', borderRadius: 12, padding: 16, border: '1px solid #e2e8f0', display: 'flex', gap: 12, alignItems: 'center' }}><div style={{ width: 40, height: 40, borderRadius: '50%', background: '#f1f5f9' }} /><div style={{ flex: 1 }}><Skeleton h={16} w="40%" style={{ marginBottom: 6 }} /><Skeleton h={12} w="60%" /></div></div>)}</div>
        : filtered.length === 0
          ? <EmptyState icon={<IdentificationBadge size={44} weight="regular" />} title="کارمندی یافت نشد" />
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filtered.map((e, i) => (
                <div key={e.id || i} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => setActionsTarget({
                    id: e.id,
                    username: e.username,
                    first_name: e.first_name,
                    last_name: e.last_name,
                    active: e.active,
                    type: 'employee',
                    department_name: e.department_name,
                    is_manager: !!e.is_manager,
                  })}
                >
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <Avatar name={e.username || '؟'} size={42} />
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{e.username}</div>
                      {(e.first_name || e.last_name) && <div style={{ fontSize: 12, color: '#94a3b8' }}>{[e.first_name, e.last_name].filter(Boolean).join(' ')}</div>}
                      {e.department_name && <div style={{ fontSize: 12, color: '#94a3b8' }}>{e.department_display_name || e.department_name}</div>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Badge variant={e.active ? 'green' : 'gray'}>{e.active ? 'فعال' : 'غیرفعال'}</Badge>
                    {e.is_manager && <Badge variant="blue">مدیر دپارتمان</Badge>}
                  </div>
                </div>
              ))}
            </div>
          )
      }

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="افزودن کارمند به دپارتمان" width={480}>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Select label="دپارتمان *" value={form.department_name} onChange={set('department_name')} error={errors.department_name || deptError}
            options={[{ value: '', label: '-- انتخاب --' }, ...departments.map((d) => ({ value: d.name, label: d.name }))]} />
          <Input label="نام کاربری *" value={form.username} onChange={set('username')} error={errors.username} />
          <Input label="نام *" value={form.first_name} onChange={set('first_name')} error={errors.first_name} />
          <Input label="نام خانوادگی *" value={form.last_name} onChange={set('last_name')} error={errors.last_name} />
          <PasswordField label="رمز عبور *" value={form.password} onChange={set('password')} error={errors.password} />
          <PasswordField label="تکرار رمز عبور *" value={form.password2} onChange={set('password2')} error={errors.password2} />
          <PhoneInput label="شماره موبایل *" value={form.phone_number} onChange={set('phone_number')} placeholder="09xxxxxxxxx" error={errors.phone_number} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn variant="secondary" onClick={() => { setAddOpen(false); setAttempted(false); setDeptError(''); }}>انصراف</Btn>
            <Btn onClick={submit} loading={saving}>افزودن</Btn>
          </div>
        </div>
      </Modal>
      <UserActionsModal target={actionsTarget} onClose={() => setActionsTarget(null)} onChanged={load} />
    </div>
  );
}
